import { useEffect, useEffectEvent, useRef, useState } from 'react';
import QRCode from 'qrcode';
import { Button } from '@trove/ui';
import {
  clearActivePairing,
  pollDevicePairing,
  rememberActivePairing,
  sleep,
  startDevicePairing,
  type PairingSession,
} from '../devicePairing';
import { useWorkspace } from '../workspace';
import styles from './PairDevice.module.css';

type Props = {
  onUnlocked: () => void;
  onRetry: () => void;
};

export function PairDevice({ onUnlocked, onRetry }: Props) {
  const { unlockFromDevicePairing } = useWorkspace();
  const [session, setSession] = useState<PairingSession | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string>();
  const [error, setError] = useState<string>();
  const [status, setStatus] = useState<'starting' | 'waiting' | 'unlocking'>('starting');
  const cancelled = useRef(false);

  const handleUnlocked = useEffectEvent(() => {
    onUnlocked();
  });

  const unlock = useEffectEvent(
    async (deviceId: string, deviceKeys: PairingSession['deviceKeys']) => {
      await unlockFromDevicePairing(deviceId, deviceKeys);
    },
  );

  useEffect(() => {
    cancelled.current = false;
    void (async () => {
      try {
        const next = await startDevicePairing();
        if (cancelled.current) return;
        rememberActivePairing({
          code: next.authorization.code,
          deviceId: next.authorization.deviceId,
          expiresAt: next.authorization.expiresAt,
        });
        setSession(next);
        const dataUrl = await QRCode.toDataURL(next.authorization.verificationUriComplete, {
          margin: 1,
          width: 220,
          color: { dark: '#173328', light: '#00000000' },
          errorCorrectionLevel: 'M',
        });
        if (cancelled.current) return;
        setQrDataUrl(dataUrl);
        setStatus('waiting');

        const expiresAt = new Date(next.authorization.expiresAt).getTime();
        const intervalMs = Math.max(2, next.authorization.interval || 3) * 1000;
        while (!cancelled.current && Date.now() < expiresAt) {
          await sleep(intervalMs);
          if (cancelled.current) return;
          const polled = await pollDevicePairing(next);
          if (polled.status === 'approved') {
            setStatus('unlocking');
            await unlock(next.authorization.deviceId, next.deviceKeys);
            clearActivePairing(next.authorization.code);
            if (!cancelled.current) handleUnlocked();
            return;
          }
          if (polled.status === 'expired' || polled.status === 'delivered') {
            clearActivePairing(next.authorization.code);
            setError(
              polled.status === 'delivered'
                ? 'This code was already used. Start again.'
                : 'This code expired. Start again.',
            );
            return;
          }
        }
        if (!cancelled.current) {
          clearActivePairing(next.authorization.code);
          setError('This code expired. Start again.');
        }
      } catch (cause) {
        if (!cancelled.current) {
          setError(cause instanceof Error ? cause.message : 'Could not start pairing');
        }
      }
    })();
    return () => {
      cancelled.current = true;
    };
  }, []);

  const connectUrl = session
    ? new URL(session.authorization.verificationUri)
    : new URL('/connect', window.location.origin);

  return (
    <div className={styles.root}>
      <div className={styles.card}>
        {status === 'starting' || !session || !qrDataUrl ? (
          <div className={styles.qrPlaceholder}>
            <span className={styles.waiting}>Preparing a one-time code…</span>
          </div>
        ) : (
          <img
            className={styles.qr}
            src={qrDataUrl}
            alt="Pairing QR code"
            width={220}
            height={220}
          />
        )}
        <div className={styles.codeRow}>
          <span className={styles.codeLabel}>Code</span>
          <span className={styles.code} aria-label="One-time code">
            {session?.authorization.code ?? '····-····'}
          </span>
        </div>
      </div>
      {error ? (
        <p className={styles.error}>{error}</p>
      ) : (
        <p className={styles.status}>
          <span className={styles.statusDot} />
          {status === 'unlocking'
            ? 'Approved — unlocking…'
            : `Waiting for approval at ${connectUrl.host}${connectUrl.pathname}`}
        </p>
      )}
      {error ? (
        <div className={styles.actions}>
          <Button variant="primary" onClick={onRetry}>
            Get a new code
          </Button>
        </div>
      ) : null}
    </div>
  );
}
